import { Link } from "react-router-dom";
import { Check, Zap, ArrowRight } from "lucide-react";
import { inr, track } from "../../lib/api";
import { useSite } from "../../context/SiteContext";
import { useCurrency } from "../../context/CurrencyContext";

const STANDARD_FEATURES = [
  "Requirement analysis", "Stakeholder consultation", "Architecture", "UI implementation",
  "AI-assisted development", "Testing", "Agreed revisions", "Deployment",
  "Source code handover", "Credentials handover",
];

export default function DeliveryModels() {
  const { settings } = useSite();
  const { money, currency } = useCurrency();
  const r = settings.rates;

  return (
    <section className="py-24 lg:py-36 border-t border-white/5" data-testid="delivery-models-section">
      <div className="container-x">
        <p className="section-label">TWO WAYS TO BUILD</p>
        <h2 className="mt-5 font-heading text-4xl sm:text-5xl font-bold tracking-tight text-white max-w-3xl">
          CHOOSE YOUR <span className="text-flame">SPEED.</span>
        </h2>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* STANDARD */}
          <div className="rounded-2xl border border-white/10 bg-navy-800 p-8 sm:p-12 card-lift" data-testid="standard-model-card">
            <p className="font-mono text-[11px] tracking-[0.25em] text-slate-400">STANDARD DELIVERY</p>
            <h3 className="mt-4 font-heading text-3xl sm:text-4xl font-extrabold text-white">BUILD IT IN DAYS.</h3>
            <p className="mt-4 text-sm text-slate-400 leading-relaxed">
              Standard Delivery is designed for businesses that want rapid development without requiring a live build session.
            </p>
            <p className="mt-8 font-heading text-4xl font-black text-white" data-testid="standard-rate-display">
              {money(r.standard)}<span className="text-base font-semibold text-slate-400"> / DEVELOPMENT HOUR</span>
            </p>
            {currency !== "INR" && (
              <p className="mt-2 text-[10px] font-mono text-slate-500">Equivalent of INR base pricing at current exchange rate.</p>
            )}
            <p className="mt-4 text-xs text-slate-500 leading-relaxed">
              Suitable applications are targeted for delivery within approximately 7 days. Many standard projects are
              targeted within approximately 50 development hours, subject to scope.
            </p>
            <div className="mt-5 rounded-lg bg-navy-900/80 border border-white/5 p-4 font-mono text-xs text-slate-400">
              50 hours × {inr(r.standard)} = <span className="text-flame font-bold">{inr(50 * r.standard)}</span>
              <span className="block mt-2 text-[10px] text-slate-600">
                Illustrative example only — not a fixed price. Final cost depends on scope, features, integrations, complexity and development hours.
              </span>
            </div>
            <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
              {STANDARD_FEATURES.map((f) => (
                <li key={f} className="flex items-center gap-2 text-xs text-slate-300">
                  <Check className="w-3.5 h-3.5 text-flame shrink-0" /> {f}
                </li>
              ))}
            </ul>
            <Link
              to="/get-estimate?model=standard"
              data-testid="standard-estimate-btn"
              onClick={() => track("estimate_click", { location: "models_standard" })}
              className="mt-10 inline-flex items-center gap-2 rounded-full border border-white/25 hover:border-flame hover:text-flame text-white font-heading font-bold text-sm tracking-wide px-7 py-3.5 transition-colors"
            >
              GET STANDARD ESTIMATE <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {/* RUSH HOUR */}
          <div className="rounded-2xl bg-navy-800 p-8 sm:p-12 tracing-beam card-lift" data-testid="rush-model-card">
            <div className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse-dot" />
              <p className="font-mono text-[11px] tracking-[0.25em] text-flame">RUSH HOUR — LIVE BUILD</p>
            </div>
            <h3 className="mt-4 font-heading text-3xl sm:text-4xl font-extrabold text-white">BUILD IT LIVE.</h3>
            <p className="mt-2 text-sm text-flame/90 font-semibold">Why wait days when the right application can be built with you, in the room?</p>
            <p className="mt-4 text-sm text-slate-400 leading-relaxed">
              Rush Hour brings our development specialist directly into the room with your stakeholders to build the application live.
            </p>
            <div className="mt-8 space-y-3">
              <div className="flex items-baseline justify-between rounded-lg bg-navy-900/80 border border-white/5 px-5 py-4">
                <span className="text-xs font-semibold tracking-wider text-slate-300">VIBE CODING SPECIALIST</span>
                <span className="font-heading text-2xl font-black text-white" data-testid="specialist-rate-display">{money(r.specialist)}<span className="text-xs font-semibold text-slate-400">/hr</span></span>
              </div>
              <div className="flex items-baseline justify-between rounded-lg bg-flame/10 border border-flame/30 px-5 py-4">
                <span className="text-xs font-semibold tracking-wider text-flame">SUPER VIBE CODER</span>
                <span className="font-heading text-2xl font-black text-white" data-testid="super-rate-display">{money(r.super)}<span className="text-xs font-semibold text-slate-400">/hr</span></span>
              </div>
            </div>
            <p className="mt-5 text-xs text-slate-500 leading-relaxed">
              Many suitable MVPs, prototypes, dashboards, portals and focused business applications may be completed in
              approximately 3–5 hours depending on scope. Rush Hour is suitable for clearly defined applications whose
              scope can reasonably fit within the selected session.
            </p>
            <Link
              to="/book-rush-hour"
              data-testid="rush-book-btn"
              onClick={() => track("rush_hour_click", { location: "models_rush" })}
              className="mt-10 inline-flex items-center gap-2 rounded-full bg-flame hover:bg-flame-hover text-white font-heading font-bold text-sm tracking-wide px-7 py-3.5 transition-colors shadow-[0_0_28px_rgba(249,115,22,0.4)]"
            >
              <Zap className="w-4 h-4" /> BOOK RUSH HOUR
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
